import { createInfiniteQuery, createMutation, createQuery, createSuspenseQuery, makeQueryKey } from "@steto-care/steto-query";
import { QueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { DiabetesRepository } from "@/io/repository/DiabetesRepository.ts";
import { InsulinRepository } from "@/io/repository/InsulinRepository.ts";
import { ManagePatientRepository } from "@/io/repository/ManagePatientRepository.ts";
import { makeCGMDailyGraphData } from "@/models/CGMDailyGraphData.ts";
import { EditBGMGlycemiaParameters, EditCGMGlycemiaParameters, GlycemiaParameters } from "@/models/DiabetesDataModel.ts";
import { DiabetesDataParams, getDiabetesDataNextPageParam, getDiabetesDataPreviousPageParam } from "@/queries/utils/DiabetesQueryUtils.ts";

type DiabetesRequest = {
  patientId: string;
  from: DateTime<true>;
  to: DateTime<true>;
};

type BGMLogbookRequest = DiabetesRequest & {
  order: "downIsFuture" | "downIsPast";
};

// When the user clicks for more data, load 1 week (business rule)
export const BGM_DOWN_IS_FUTURE_WEEKS = 1;
// When the user scrolls to the bottom, load 6 weeks (arbitrary number, find balance between request speed and scroll locking at the bottom)
export const BGM_DOWN_IS_PAST_WEEKS = 6;

type GlycemiaIndicatorRequest = {
  patientId: string;
  from: DateTime<true>;
  to: DateTime<true>;
  glycemiaMeasurementMethod: string;
};

type SyncPatientDataRequest = {
  patientId: string;
  // Not part of the query key: called right before a sync starts (it can take up to 2 minutes)
  onSyncStart?: () => void;
};

export class DiabetesQueries {
  constructor(
    private readonly diabetes: DiabetesRepository = new DiabetesRepository(),
    private readonly insulin: InsulinRepository = new InsulinRepository(),
    private readonly patient: ManagePatientRepository = new ManagePatientRepository(),
  ) {}

  /*******************************/
  /*********** QUERIES ***********/
  /*******************************/

  useInfiniteCGMDailyGraph = createInfiniteQuery(
    "infinite-cgm-daily-graph",
    ({ patientId, from, to }) => makeQueryKey(patientId, from?.toISO(), to?.toISO()),
    async ({ patientId, from, to }: DiabetesDataParams) => {
      const start = from.startOf("day");
      const end = to.endOf("day");
      const [
        parametersResult,
        glycemiaResult,
        insulinResult,
        foodResult,
        activityResult,
        reportsResult,
        insulinPumpEvents,
        insulinBasalRates,
        closedLoopEvents,
      ] = await Promise.all([
        this.diabetes.getCGMParameters(patientId),
        this.diabetes.getGlycemia(patientId, start, end),
        this.insulin.getInsulin(patientId, start, end),
        this.diabetes.getFood(patientId, start, end),
        this.diabetes.getActivity(patientId, start, end),
        this.diabetes.getReports(patientId, start, end),
        this.insulin.getInsulinPumpEvents(patientId, start, end),
        this.insulin.getInsulinBasalRates(patientId, start, end),
        this.insulin.getClosedLoopEvents(patientId, start, end),
      ]);
      return makeCGMDailyGraphData(
        start,
        end,
        parametersResult,
        glycemiaResult,
        insulinResult,
        foodResult,
        activityResult,
        reportsResult,
        insulinPumpEvents,
        insulinBasalRates,
        closedLoopEvents,
      );
    },
    (_, __, lastPageParam) => getDiabetesDataNextPageParam(lastPageParam),
    {
      getPreviousPageParam: (_, __, firstPageParam) => getDiabetesDataPreviousPageParam(firstPageParam),
      maxPages: 50,
    },
  );

  useSyncPatientData = createQuery<SyncPatientDataRequest, boolean>(
    "sync-patient-data",
    ({ patientId }) => makeQueryKey(patientId),
    async ({ patientId, onSyncStart }, { client }) => {
      const { hasDataToSync } = await this.diabetes.hasDataToSync(patientId);
      if (!hasDataToSync) {
        return false;
      }
      onSyncStart?.();
      const { synced } = await this.diabetes.syncData(patientId);
      if (synced) {
        this.invalidateAllStats(client, patientId);
        this.useMedicalDevices.invalidate(client, patientId);
      }
      return synced;
    },
    { retry: false },
  );

  useInfiniteBGMLogbook = createInfiniteQuery(
    "infinite-bgm-logbook",
    ({ patientId, order, from, to }) => makeQueryKey(patientId, order, from?.toISO(), to?.toISO()),
    async ({ patientId, from, to }: BGMLogbookRequest) => this.diabetes.getBGMLogbook(patientId, from, to),
    (_, __, lastPageParam) => {
      const to = lastPageParam.from.minus({ day: 1 });
      const from =
        lastPageParam.order === "downIsPast"
          ? lastPageParam.from.minus({ week: BGM_DOWN_IS_PAST_WEEKS }).startOf("week")
          : lastPageParam.from.minus({ week: BGM_DOWN_IS_FUTURE_WEEKS });
      return {
        patientId: lastPageParam.patientId,
        to,
        from,
        order: lastPageParam.order,
      };
    },
    {
      getPreviousPageParam: (_, __, firstPageParam) => {
        const from = firstPageParam.to.plus({ day: 1 });
        // When previous page only load one more week even for infinite list
        let to = firstPageParam.to.plus({ week: BGM_DOWN_IS_FUTURE_WEEKS });
        const nextDay = DateTime.now().plus({ days: 1 }).startOf("day");
        if (from >= nextDay) {
          return null;
        }
        if (to >= nextDay) {
          to = DateTime.now().startOf("day");
        }
        return {
          patientId: firstPageParam.patientId,
          to,
          from,
          order: firstPageParam.order,
        };
      },
    },
  );

  useFileReport = createSuspenseQuery(
    "file-report",
    ({ patientId, date }) => makeQueryKey(patientId, date?.toISODate() ?? undefined),
    async ({ patientId, date }: { patientId: string; date: DateTime }) =>
      this.diabetes.getDailyFileReport(patientId, date),
  );

  useParameters = createQuery(
    "parameters",
    patientId => [patientId],
    async (patientId: string): Promise<GlycemiaParameters> => {
      const displayMode = await this.patient.getDiabetesDisplayMode(patientId);
      if (displayMode.glycemiaDataDisplayMode === "logbook") {
        const parameters = await this.diabetes.getBGMParameters(patientId);
        return { type: "BGM", parameters };
      } else {
        const parameters = await this.diabetes.getCGMParameters(patientId);
        return { type: "CGM", parameters };
      }
    },
  );

  useMedicalDevices = createSuspenseQuery(
    "medical-devices",
    patientId => [patientId],
    async (patientId: string) => this.diabetes.getMedicalDevices(patientId),
  );

  useGlycemiaIndicators = createQuery(
    "glycemia-indicators",
    ({ patientId, from, to, glycemiaMeasurementMethod }) =>
      makeQueryKey(patientId, from?.toISODate(), to?.toISODate(), glycemiaMeasurementMethod),
    async ({ patientId, from, to, glycemiaMeasurementMethod }: GlycemiaIndicatorRequest) =>
      await this.diabetes.getGlycemiaIndicators(patientId, from, to, glycemiaMeasurementMethod),
  );

  useInsulinDiscreteIndicators = createQuery(
    "insulin-indicators",
    ({ patientId, from, to }) => makeQueryKey(patientId, from?.toISODate(), to?.toISODate()),
    async ({ patientId, from, to }: DiabetesDataParams) =>
      await this.diabetes.getInsulinDiscreteIndicators(patientId, from, to),
  );

  useInsulinContinuousIndicators = createQuery(
    "insulin-continuous-indicators",
    ({ patientId, from, to }) => makeQueryKey(patientId, from?.toISODate(), to?.toISODate()),
    async ({ patientId, from, to }: DiabetesDataParams) =>
      await this.diabetes.getInsulinContinuousIndicators(patientId, from, to),
  );

  usePumpModeIndicators = createQuery(
    "pump-mode-indicators",
    ({ patientId, from, to }) => makeQueryKey(patientId, from?.toISODate(), to?.toISODate()),
    async ({ patientId, from, to }: DiabetesDataParams) =>
      await this.diabetes.getPumpModeIndicators(patientId, from, to),
  );

  useGetDexcomAuthUrl = createQuery(
    "get-dexcom-auth-url",
    () => makeQueryKey(),
    async () => await this.diabetes.getDexcomAuthorizeURL(),
    { gcTime: 0 },
  );

  /*******************************/
  /*********** MUTATIONS *********/
  /*******************************/

  useEditCGMParameters = createMutation(
    "edit-cgm-parameters",
    async (data: EditCGMGlycemiaParameters) => this.diabetes.editCGMParameters(data),
    {
      onSuccess: (parameters, _, { client }) => {
        this.useParameters.manualUpdate(client, parameters.id, () => ({
          type: "CGM",
          parameters,
        }));
        this.invalidateAllStats(client, parameters.id);
      },
    },
  );

  useEditBGMParameters = createMutation(
    "edit-bgm-parameters",
    async (data: EditBGMGlycemiaParameters) => this.diabetes.editBGMParameters(data),
    {
      onSuccess: (parameters, _, { client }) => {
        this.useParameters.manualUpdate(client, parameters.id, () => ({
          type: "BGM",
          parameters,
        }));
        this.invalidateAllStats(client, parameters.id);
      },
    },
  );

  useDexcomCallback = createMutation("dexcom-callback", async (code: string) => this.diabetes.dexcomAuthCallback(code));

  /*******************************/
  /************ HELPERS **********/
  /*******************************/

  invalidateAllStats = (queryClient: QueryClient, patientId: string) => {
    this.useInfiniteCGMDailyGraph.invalidate(queryClient, { patientId });
    this.useGlycemiaIndicators.invalidate(queryClient, { patientId });
    this.useInsulinDiscreteIndicators.invalidate(queryClient, { patientId });
    this.useInsulinContinuousIndicators.invalidate(queryClient, { patientId });
    this.usePumpModeIndicators.invalidate(queryClient, { patientId });
  };
}
